/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';
import { createPortal } from 'react-dom';

import type { DrawerProps } from '../../types';
import Icon from '../Icon/Icon';
import useResizable from '../../hooks/useResizable';
import './Drawer.less';

const WIDTH_PRESETS: Record<string, number> = {
  standard: 350,
  large: 550,
  xlarge: 900,
};

const UNSET_STORAGE_KEY = 'ds-drawer-width:unset';
const MIN_WIDTH = 250;
const MAX_WIDTH_FRACTION = 0.9;
const FOCUSABLE_SELECTOR =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function usePersistedWidth(key: string, defaultValue: number | null): [number | null, (v: number) => void] {
  const [value, setState] = React.useState<number | null>(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setValue = React.useCallback(
    (v: number) => {
      setState(v);
      try {
        localStorage.setItem(key, JSON.stringify(v));
      } catch {}
    },
    [key]
  );

  return [value, setValue];
}

const Drawer = React.forwardRef<HTMLDivElement, DrawerProps>(
  (
    {
      open = false,
      onClose,
      title,
      footer,
      width = 'standard',
      widthStorageKey,
      resizable = false,
      placement = 'right',
      mask = true,
      maskClosable = true,
      closable = true,
      destroyOnClose = false,
      noPadding = false,
      compact = false,
      headerExtra,
      className,
      children,
    },
    ref
  ) => {
    const drawerRef = React.useRef<HTMLDivElement>(null);
    const previousFocusRef = React.useRef<HTMLElement | null>(null);

    const baseWidth = React.useMemo(() => {
      if (width === 'full') return window.innerWidth;
      if (typeof width === 'string' && WIDTH_PRESETS[width] !== undefined) return WIDTH_PRESETS[width];
      return width as number;
    }, [width]);

    const storageKey = widthStorageKey ?? UNSET_STORAGE_KEY;
    const [persistedWidth, setPersistedWidth] = usePersistedWidth(storageKey, null);

    const activeWidth =
      resizable && widthStorageKey && persistedWidth !== null
        ? Math.min(persistedWidth, window.innerWidth * MAX_WIDTH_FRACTION)
        : baseWidth;

    const { isDragging, handleResizeMouseDown } = useResizable({
      baseWidth: typeof baseWidth === 'number' ? baseWidth : window.innerWidth * MAX_WIDTH_FRACTION,
      currentWidth: widthStorageKey ? persistedWidth : null,
      onWidthChange: widthStorageKey ? setPersistedWidth : undefined,
      minWidth: MIN_WIDTH,
      maxWidthFraction: MAX_WIDTH_FRACTION,
    });

    // Body scroll lock
    React.useEffect(() => {
      if (!open) return;
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }, [open]);

    // Escape key
    React.useEffect(() => {
      if (!open || !closable) return;
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose?.();
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, closable, onClose]);

    // Focus trap
    React.useEffect(() => {
      if (!open) return;
      const activeEl = document.activeElement;
      if (activeEl instanceof HTMLElement) {
        previousFocusRef.current = activeEl;
      }
      const container = drawerRef.current;
      if (!container) return;

      const getFocusables = () => Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

      const handleTab = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        const focusables = getFocusables();
        if (!focusables.length) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };

      document.addEventListener('keydown', handleTab);
      getFocusables()[0]?.focus();

      return () => {
        document.removeEventListener('keydown', handleTab);
        previousFocusRef.current?.focus();
      };
    }, [open]);

    const hasHeader = !!(title || closable || headerExtra);

    const setRefs = (node: HTMLDivElement | null) => {
      (drawerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    };

    const widthStyle = typeof activeWidth === 'number' ? `${activeWidth}px` : activeWidth;

    const content = (
      <>
        {mask && (
          <div
            data-testid="ds-drawer-mask"
            className={cx('ds-drawer__mask', { 'ds-drawer__mask--open': open })}
            onClick={maskClosable ? onClose : undefined}
          />
        )}
        <div
          ref={setRefs}
          data-testid="ds-drawer"
          className={cx(
            'ds-drawer',
            `ds-drawer--${placement}`,
            {
              'ds-drawer--open': open,
              'ds-drawer--resizable': resizable,
              'ds-drawer--has-header': hasHeader,
              'ds-drawer--has-footer': !!footer,
            },
            className
          )}
          style={{ width: widthStyle }}
          role="dialog"
          aria-modal="true"
          aria-label={typeof title === 'string' ? title : undefined}>
          {resizable && (
            <div
              data-testid="ds-drawer-resize-handle"
              className={cx('ds-drawer__resize-handle', {
                'ds-drawer__resize-handle--dragging': isDragging,
              })}
              onMouseDown={handleResizeMouseDown}
            />
          )}
          {hasHeader && (
            <div className="ds-drawer__header">
              {title && <span className="ds-drawer__title">{title}</span>}
              {(headerExtra || closable) && (
                <div className="ds-drawer__header-actions">
                  {headerExtra}
                  {closable && (
                    <button type="button" className="ds-drawer__close" onClick={onClose} aria-label="Close drawer">
                      <Icon name="actionable/close" size="small" />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          <div
            className={cx('ds-drawer__body', {
              'ds-drawer__body--no-padding': noPadding,
              'ds-drawer__body--compact': compact,
            })}>
            {!destroyOnClose || open ? children : null}
          </div>
          {footer && (
            <div data-testid="ds-drawer-footer" className="ds-drawer__footer">
              {footer}
            </div>
          )}
        </div>
      </>
    );

    return createPortal(content, document.body);
  }
);

Drawer.displayName = 'Drawer';

export default Drawer;
